import { t } from '@/i18n';
import { DateRangeSchema } from '@/utils';
import { z } from 'zod';
import { NAME_MAX_LENGTH } from '../utils';
import { NationalExpenditureNatureModel } from './national-expenditure-nature';

export const ExpenditureNatureBaseModel = z.object({
  id: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
  code: z.number().int().min(1),
  externalSystemCode: z.string().nullable(),
  aggregateByExpenditureNatureForExternalSystem: z.boolean(),
  aggregationName: z.string().nullish(),
  name: z.string().min(1),
  startYear: z.number().int().nullable(),
  endYear: z.number().int().nullable(),
  level: z.union([z.literal(0), z.literal(1), z.literal(2)]),
  upperId: z.string().uuid().nullable(),
  nationalExpenditureNatureId: z.string().uuid().nullish(),
  nationalExpenditureNature: NationalExpenditureNatureModel.nullish(),
});

const UpperExpenditureNatureModel = ExpenditureNatureBaseModel.omit({
  nationalExpenditureNature: true,
});

type ExpenditureNature = z.infer<typeof ExpenditureNatureBaseModel> & {
  upper: z.infer<typeof UpperExpenditureNatureModel> | null;
};

const ExpenditureNatureModel: z.ZodType<ExpenditureNature> =
  ExpenditureNatureBaseModel.extend({
    upper: z.lazy(() => UpperExpenditureNatureModel.nullable()),
  });

export const ExpenditureNaturesModel = ExpenditureNatureModel.array();

const ExpenditureNatureFormBaseSchema = ExpenditureNatureBaseModel.pick({
  level: true,
  code: true,
  aggregateByExpenditureNatureForExternalSystem: true,
  aggregationName: true,
}).extend({
  name: ExpenditureNatureBaseModel.shape.name.max(NAME_MAX_LENGTH),
  externalSystemCode:
    ExpenditureNatureBaseModel.shape.externalSystemCode.nullish(),
  period: DateRangeSchema.nullish(),
  upperId: ExpenditureNatureBaseModel.shape.upperId.optional(),
  nationalExpenditureNatureId:
    ExpenditureNatureBaseModel.shape.nationalExpenditureNatureId.optional(),
});

export const ExpenditureNatureFormSchema =
  ExpenditureNatureFormBaseSchema.refine(
    (data) => !(data.level === 1 && !data.nationalExpenditureNatureId),
    {
      path: ['nationalExpenditureNatureId'],
      message: t('validation.not-selected'),
    },
  ).refine((data) => !(data.level !== 1 && !data.upperId), {
    path: ['upperId'],
    message: t('validation.not-selected'),
  });

export const UpdateExpenditureNatureParamsSchema =
  ExpenditureNatureFormBaseSchema.extend({
    id: z.string().uuid(),
  });
